import { useCallback, useEffect, useMemo, useState } from 'react';

import {
	type ColumnLayout,
	type ColumnLayoutStore,
	getDefaultColumnLayoutStore,
} from '@/hooks/use-column-layout';

/**
 * Named column-layout snapshots, persisted per-report. Applied by the column
 * picker as a one-shot "set working state to this layout" — never auto-persisted
 * as the active layout. The user still has to click Apply to commit.
 *
 * Stored shape: `Array<{ id, name, layout, createdAt }>` under
 * `reports:<id>:columnPresets:v1`. We keep the same key convention as
 * `useColumnLayout` so presets are scoped naturally to one report.
 */

export interface ColumnPreset {
	id: string;
	name: string;
	layout: ColumnLayout;
	createdAt: string;
}

export interface UseColumnPresetsOptions {
	storageKey: string;
	store?: ColumnLayoutStore;
}

export interface UseColumnPresetsReturn {
	presets: ColumnPreset[];
	/** Save a new preset under `name`. Trims whitespace; rejects empty names. */
	savePreset: (name: string, layout: ColumnLayout) => ColumnPreset | undefined;
	/** Delete a preset by id. No-op if the id isn't present. */
	deletePreset: (id: string) => void;
}

function parsePresets(raw: string | null): ColumnPreset[] {
	if (!raw) return [];
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return [];
	}
	if (!Array.isArray(parsed)) return [];
	const out: ColumnPreset[] = [];
	for (const item of parsed) {
		if (!item || typeof item !== 'object') continue;
		const p = item as { id?: unknown; name?: unknown; layout?: unknown; createdAt?: unknown };
		if (typeof p.id !== 'string' || typeof p.name !== 'string' || typeof p.createdAt !== 'string') continue;
		if (!p.layout || typeof p.layout !== 'object') continue;
		const l = p.layout as { visible?: unknown; hidden?: unknown };
		if (!Array.isArray(l.visible) || !Array.isArray(l.hidden)) continue;
		const visible = l.visible.filter((x): x is string => typeof x === 'string');
		const hidden = l.hidden.filter((x): x is string => typeof x === 'string');
		out.push({ id: p.id, name: p.name, layout: { visible, hidden }, createdAt: p.createdAt });
	}
	return out;
}

function makeId(): string {
	if (typeof globalThis !== 'undefined'
		&& typeof (globalThis as { crypto?: { randomUUID?: () => string } }).crypto?.randomUUID === 'function') {
		return (globalThis as { crypto: { randomUUID: () => string } }).crypto.randomUUID();
	}
	return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function useColumnPresets(options: UseColumnPresetsOptions): UseColumnPresetsReturn {
	const { storageKey } = options;
	const store = options.store ?? getDefaultColumnLayoutStore();

	const [presets, setPresets] = useState<ColumnPreset[]>(
		() => parsePresets(store.getItem(storageKey)),
	);

	useEffect(() => {
		setPresets(parsePresets(store.getItem(storageKey)));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storageKey]);

	const persist = useCallback((next: ColumnPreset[]) => {
		setPresets(next);
		if (next.length === 0) {
			store.removeItem(storageKey);
		} else {
			store.setItem(storageKey, JSON.stringify(next));
		}
	}, [store, storageKey]);

	const savePreset = useCallback((name: string, layout: ColumnLayout): ColumnPreset | undefined => {
		const trimmed = name.trim();
		if (!trimmed) return undefined;
		const next: ColumnPreset = {
			id: makeId(),
			name: trimmed,
			layout: { visible: [...layout.visible], hidden: [...layout.hidden] },
			createdAt: new Date().toISOString(),
		};
		// If a preset with the same (case-insensitive) name exists, replace it
		// in place so users can iterate without piling up duplicates. We keep
		// the original id, createdAt, AND original casing of the name so the
		// chip doesn't mutate visually on a quick re-save.
		const lc = trimmed.toLowerCase();
		const existingIdx = presets.findIndex((p) => p.name.toLowerCase() === lc);
		const updated = [...presets];
		if (existingIdx >= 0) {
			const existing = presets[existingIdx];
			updated[existingIdx] = {
				id: existing.id,
				name: existing.name,
				createdAt: existing.createdAt,
				layout: next.layout,
			};
		} else {
			updated.push(next);
		}
		persist(updated);
		return existingIdx >= 0 ? updated[existingIdx] : next;
	}, [presets, persist]);

	const deletePreset = useCallback((id: string) => {
		const filtered = presets.filter((p) => p.id !== id);
		if (filtered.length === presets.length) return;
		persist(filtered);
	}, [presets, persist]);

	return useMemo(() => ({ presets, savePreset, deletePreset }), [presets, savePreset, deletePreset]);
}
